import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractErroApi, getErrorMessage } from '@/shared/lib/api/errors';
import {
  assistenteService,
  type AnexoMia,
  type AssistenteConfirmacao,
  type AssistenteHistorico,
  type AssistenteLink,
  type AssistenteMensagemResponse,
  type AssistenteProposta,
  type BlocoMia,
  type ContextoTela,
} from './assistenteService';
import { coletarAlvos, executarAcaoInterface } from './acoesTelaMia';
import { useAssistenteUi } from './assistenteUi';
import { lerBlocos } from './lerBlocosMia';

export type ItemMia = {
  id: string;
  /** id gravado na API; so a fala da Mia com id recebe curtir. */
  idMensagem: number | null;
  papel: 'usuario' | 'assistente';
  texto: string;
  links?: AssistenteLink[];
  blocos: BlocoMia[];
  nota: -1 | 1 | null;
  proposta?: AssistenteProposta | null;
  propostaEstado?: 'pendente' | 'confirmada' | 'cancelada';
  /** Depois do Confirmar: o link do que foi gravado e, no lote, quem ficou de fora. */
  resultado?: ResultadoPropostaMia;
  /** Fotos e PDFs da fala da pessoa. */
  anexos?: AnexoMia[];
  /** Acesso recusado porque um dia anterior ficou com entrada sem saida. */
  diaAberto?: DiaAbertoMia;
};

export type DiaAbertoMia = { data: string; horaEntrada: string; mensagem: string };

function lerDiaAberto(error: unknown): DiaAbertoMia | null {
  const erro = extractErroApi(error);
  if (erro?.codigo !== 'ACESSO_DIA_ANTERIOR_ABERTO') return null;
  const detalhes = erro.detalhes as { dataAberta?: unknown; horaEntrada?: unknown } | null | undefined;
  if (typeof detalhes?.dataAberta !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(detalhes.dataAberta)) return null;
  return {
    data: detalhes.dataAberta,
    horaEntrada: typeof detalhes.horaEntrada === 'string' ? detalhes.horaEntrada : '',
    mensagem: erro.mensagem,
  };
}

/** "Registrar a entrada de Ana hoje." -> "registrar a entrada de Ana hoje" */
function frase(texto: string): string {
  const limpo = texto.trim().replace(/[.\s]+$/, '');
  return limpo.charAt(0).toLowerCase() + limpo.slice(1);
}

/** 2026-10-13 -> 13/10/2026 */
export function dataBr(iso: string): string {
  return iso.split('-').reverse().join('/');
}

export type ResultadoPropostaMia = {
  caminho: string | null;
  rotulo: string;
  detalhes: string[];
};

function resultadoDe(proposta: AssistenteProposta, confirmacao: AssistenteConfirmacao): ResultadoPropostaMia {
  const caminho =
    confirmacao.caminho ??
    (confirmacao.idPublicacao && confirmacao.idAluno
      ? `/publicacoes?idPublicacao=${confirmacao.idPublicacao}&idAluno=${confirmacao.idAluno}&data=${confirmacao.data}`
      : null);
  const rotulo =
    confirmacao.rotulo ??
    (proposta.ferramenta === 'propor_acesso' && proposta.titulo ? `${proposta.titulo} registrada.` : 'Recado publicado.');
  return { caminho, rotulo, detalhes: confirmacao.detalhes ?? [] };
}

import { CHAVE_CONVERSAS } from './HistoricoMia';

function itensDoHistorico(historico: AssistenteHistorico): ItemMia[] {
  return historico.mensagens.map((mensagem) => ({
    id: `h-${mensagem.id}`,
    idMensagem: mensagem.id,
    papel: mensagem.papel === 'USUARIO' ? 'usuario' : 'assistente',
    texto: mensagem.texto,
    links: mensagem.links ?? [],
    blocos: lerBlocos(mensagem.blocos),
    nota: mensagem.nota === 1 || mensagem.nota === -1 ? mensagem.nota : null,
    anexos: mensagem.anexos ?? [],
  }));
}

/** Tela aberta agora, mandada como pista para a Mia (nao da permissao). */
export function contextoDaTela(
  pathname: string,
  search: string,
  titulo: string,
  alvos: { id: string; rotulo?: string }[] = [],
): ContextoTela | null {
  const rota = `${pathname}${search}`.slice(0, 300);
  if (!/^\/[A-Za-z0-9/_\-?=&.%]*$/.test(rota)) return null;
  const limpo = titulo.replace(/\s+/g, ' ').trim().slice(0, 120);
  const contexto: ContextoTela = limpo ? { rota, titulo: limpo } : { rota };
  return alvos.length ? { ...contexto, alvos } : contexto;
}

export function useConversaMia(aberto: boolean) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { recolher } = useAssistenteUi();
  /** Registros que ficaram para depois de fechar um dia anterior (R21). */
  const filaRetomar = useRef<string[]>([]);
  const [idConversa, setIdConversa] = useState<number | null>(null);
  const [titulo, setTitulo] = useState<string | null>(null);
  const [itens, setItens] = useState<ItemMia[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [etapa, setEtapa] = useState<string | null>(null);
  /** Fala da Mia chegando em pedacos; some quando a resposta final chega. */
  const [rascunho, setRascunho] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [historicoPronto, setHistoricoPronto] = useState(false);
  const historicoPedido = useRef(false);
  const geracao = useRef(0);

  useEffect(() => {
    if (!aberto || historicoPedido.current) return;
    historicoPedido.current = true;
    const minha = geracao.current;
    assistenteService
      .historico()
      .then((historico) => {
        if (geracao.current !== minha) return;
        setIdConversa(historico.idConversa > 0 ? historico.idConversa : null);
        setTitulo(historico.titulo ?? null);
        setItens((atual) => (atual.length > 0 ? atual : itensDoHistorico(historico)));
      })
      .catch(() => undefined)
      .finally(() => setHistoricoPronto(true));
  }, [aberto]);

  const abrirConversa = useCallback(async (id: number) => {
    geracao.current += 1;
    const minha = geracao.current;
    setErro(null);
    setHistoricoPronto(false);
    try {
      const historico = await assistenteService.historicoConversa(id);
      if (geracao.current !== minha) return;
      setIdConversa(historico.idConversa);
      setTitulo(historico.titulo ?? null);
      setItens(itensDoHistorico(historico));
    } catch (error) {
      if (geracao.current === minha) setErro(getErrorMessage(error, 'Nao foi possivel abrir a conversa.'));
    } finally {
      if (geracao.current === minha) setHistoricoPronto(true);
    }
  }, []);

  const novaConversa = useCallback(async () => {
    geracao.current += 1;
    historicoPedido.current = true;
    setErro(null);
    setItens([]);
    setTitulo(null);
    setIdConversa(null);
    setHistoricoPronto(true);
    try {
      const nova = await assistenteService.novaConversa();
      setIdConversa(nova.id);
    } catch {
      // Sem conversa nova, a primeira fala cai na mais recente pela rota antiga.
    }
  }, []);

  /** Recebe a resposta (fala nova ou refeita) mostrando etapas e o texto em pedacos. */
  async function receber(
    pedir: (onEtapa: (texto: string) => void, onTexto: (delta: string) => void) => Promise<AssistenteMensagemResponse>,
    aoChegar: (item: ItemMia, resposta: AssistenteMensagemResponse) => void,
    falha: string,
  ) {
    setEnviando(true);
    setRascunho('');
    const minha = geracao.current;
    try {
      const resposta = await pedir(
        (texto) => {
          if (geracao.current !== minha) return;
          setEtapa(texto);
          // Etapa nova = a Mia voltou a consultar; o que ela escrevia antes nao vale.
          setRascunho('');
        },
        (delta) => {
          if (geracao.current === minha) setRascunho((atual) => atual + delta);
        },
      );
      if (geracao.current !== minha) return;
      const blocos = lerBlocos(resposta.blocos);
      setIdConversa(resposta.idConversa);
      aoChegar(
        {
          id: `a-${resposta.idMensagem}`,
          idMensagem: resposta.idMensagem || null,
          papel: 'assistente',
          texto: resposta.texto,
          links: resposta.links ?? [],
          blocos,
          nota: null,
          proposta: resposta.proposta,
          propostaEstado: resposta.proposta ? 'pendente' : undefined,
        },
        resposta,
      );
      // Abrir tela, abrir registro e destacar acontecem sozinhos; trocar escola espera o Confirmar.
      for (const bloco of blocos) {
        if (bloco.tipo === 'acao_interface') executarAcaoInterface(bloco.acao, navigate, recolher);
      }
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONVERSAS });
    } catch (error) {
      if (geracao.current === minha) setErro(getErrorMessage(error, falha));
    } finally {
      if (geracao.current === minha) {
        setEnviando(false);
        setEtapa(null);
        setRascunho('');
      }
    }
  }

  async function enviar(fala: string, anexos: AnexoMia[] = []) {
    setErro(null);
    setItens((atual) => [
      ...atual,
      { id: `u-${Date.now()}`, idMensagem: null, papel: 'usuario', texto: fala, blocos: [], nota: null, anexos },
    ]);
    const contexto = contextoDaTela(location.pathname, location.search, document.title, coletarAlvos());
    const ids = anexos.map((anexo) => anexo.idArquivo);
    await receber(
      (onEtapa, onTexto) => assistenteService.enviarComEtapas(fala, idConversa, contexto, onEtapa, onTexto, ids),
      (item) => {
        setTitulo((atual) => atual ?? fala.split('\n')[0].slice(0, 80));
        setItens((atual) => [...atual, item]);
      },
      'Nao foi possivel falar com a Mia agora.',
    );
  }

  /** Refaz a resposta: a antiga some do fio quando a nova chega. */
  async function regenerar(itemId: string, idMensagem: number) {
    setErro(null);
    await receber(
      (onEtapa, onTexto) => assistenteService.regenerarComEtapas(idMensagem, onEtapa, onTexto),
      (item) => setItens((atual) => [...atual.filter((antigo) => antigo.id !== itemId), item]),
      'Nao foi possivel refazer a resposta.',
    );
  }

  async function avaliar(itemId: string, idMensagem: number, nota: -1 | 0 | 1, motivo?: string) {
    try {
      await assistenteService.avaliar(idMensagem, nota, motivo);
      setItens((atual) =>
        atual.map((item) => (item.id === itemId ? { ...item, nota: nota === 0 ? null : nota } : item)),
      );
    } catch {
      setErro('Nao foi possivel registrar a avaliacao.');
    }
  }

  /** Devolve false se a API recusou, para o cartao continuar aberto na edicao. */
  async function confirmar(itemId: string, proposta: AssistenteProposta, campos?: Record<string, string>) {
    setErro(null);
    try {
      const confirmacao = await assistenteService.confirmar(proposta.id, campos);
      filaRetomar.current = filaRetomar.current.filter((pedido) => pedido !== frase(proposta.texto));
      // A tela aberta (Inicio, Atividades, Agenda) mostra o que acabou de ser gravado.
      void queryClient.invalidateQueries();
      setItens((atual) =>
        atual.map((item) =>
          item.id === itemId
            ? { ...item, propostaEstado: 'confirmada', resultado: resultadoDe(proposta, confirmacao) }
            : item,
        ),
      );
      return true;
    } catch (error) {
      const diaAberto = lerDiaAberto(error);
      if (diaAberto) {
        // O cartao mostra o motivo e oferece fechar aquele dia.
        setItens((atual) => atual.map((item) => (item.id === itemId ? { ...item, diaAberto } : item)));
      } else {
        setErro(getErrorMessage(error, 'Nao foi possivel confirmar.'));
      }
      return false;
    }
  }

  /**
   * Fecha o dia que ficou aberto: cancela o registro travado e pede a saida daquele dia
   * a Mia, que pergunta o horario (R21).
   */
  async function fecharDiaAberto(itemId: string, proposta: AssistenteProposta, dia: DiaAbertoMia) {
    await cancelar(itemId, proposta);
    // Dias abertos em sequencia: a fila guarda tudo o que ficou para depois, do mais novo ao pedido original.
    const pedido = frase(proposta.texto);
    if (!filaRetomar.current.includes(pedido)) filaRetomar.current = [pedido, ...filaRetomar.current];
    const entrada = dia.horaEntrada ? ` (a entrada foi às ${dia.horaEntrada})` : '';
    await enviar(
      `Registrar a saída de ${proposta.nomeAluno} no dia ${dataBr(dia.data)}${entrada}. ` +
        `Depois, ${filaRetomar.current.join('; depois, ')}.`,
    );
  }

  async function cancelar(itemId: string, proposta: AssistenteProposta) {
    setErro(null);
    try {
      await assistenteService.cancelar(proposta.id);
      setItens((atual) =>
        atual.map((item) => (item.id === itemId ? { ...item, propostaEstado: 'cancelada' } : item)),
      );
    } catch {
      setErro('Nao foi possivel cancelar.');
    }
  }

  return {
    idConversa,
    titulo,
    itens,
    enviando,
    etapa,
    rascunho,
    erro,
    setErro,
    historicoPronto,
    enviar,
    regenerar,
    avaliar,
    confirmar,
    cancelar,
    fecharDiaAberto,
    abrirConversa,
    novaConversa,
  };
}

/** Conversa em markdown, para baixar. */
export function exportarConversa(titulo: string | null, itens: ItemMia[]): string {
  const cabeca = `# ${titulo || 'Conversa com a Mia'}\n\n`;
  return (
    cabeca +
    itens
      .map((item) => `**${item.papel === 'usuario' ? 'Você' : 'Mia'}:**\n\n${item.texto.trim()}\n`)
      .join('\n')
  );
}
